<template>
  <DropdownWithOverlay 
    v-model="isPickerOpen" 
    position="dropdown-start"
    :keep-open="true"
    content-class="bg-base-100 !border-none rounded-box shadow-lg border border-base-300 w-[90%] sm:w-[30rem] !fixed sm:absolute left-0 sm:left-auto right-0 sm:right-auto sm:right-auto mx-auto"
    overlay-class="bg-gradient-to-l from-black/10 to-transparent backdrop-blur-sm"
    v-if="modelValue"
  >
    <template #trigger>
      <div
        class="block flex items-center relative group"
        :class="{ 'borderless': borderless }"
        role="group"
        style="width: 100% !important"
      >
        <v-icon 
          class="transform-color duration-1000 group-hover:scale-105 text-base-content/20 group-hover:text-accent absolute left-2" 
          scale="1.2" 
          name="bi-calendar3" 
        />
        
        <!-- Simple button showing current date range -->
        <button 
          class="w-full text-left pl-10 pr-3 py-3 border border-zinc-300 dark:border-base-100 rounded-lg text-base-content/80 text-xs bg-transparent hover:border-accent transition-colors"
          :class="{ 
            '!border-transparent dark:!border-transparent': borderless 
          }"
        >
          {{ displayValue || 'Select date range' }}
        </button>
      </div>
    </template>
    
    <template #content="{ close }">
      <div>
        <VueCtkDateTimePicker
          class="relative"
        :initial-value="dateRange"
        v-model="mutableValue"
        format="YYYY-MM-DD hh:mm a"
        formatted="L"
        :range="true"
        :only-time="false"
        :inline="true"
        :no-shortcuts="false"
        :noClearButton="true"
        :no-value-to-custom-elem="true"
        :no-header="true"
        :no-button="true"
        :auto-close="true"
        :color="'oklch(var(--a))'"
        :custom-shortcuts="ranges"
        ref="input"
        label=""
        :dark="isDark"
        locale="en"
      >
      </VueCtkDateTimePicker>
      </div>
    </template>
  </DropdownWithOverlay>
</template>

<script setup>
import { ref, computed } from 'vue'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { useToggleThemeStore } from "@/stores/toggleTheme"
import DropdownWithOverlay from '@/components/common/DropdownWithOverlay.vue'

const themeStore = useToggleThemeStore();

const isDark = computed(() => themeStore.activeTheme == 'night')

const props = defineProps({
  borderless: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Object,
  },
  ranges: {
    type: Array,
    default: () => [
      { key: 'today', label: 'Today', value: 'day' },
      {
        key: 'yesterday',
        label: 'Yesterday',
        value: '-day',
        isSelected: false,
      },
      {
        key: 'weekToDate',
        label: 'Week to Date',
        value: 'week',
        isSelected: false,
      },
      {
        key: 'monthToDate',
        label: 'Month to Date',
        value: 'month',
        isSelected: false,
      },
      {
        key: 'last7Days',
        label: 'Last 7 days',
        value: 7,
        isSelected: false,
      },
      {
        key: 'last14Days',
        label: 'Last 14 days',
        value: 14,
        isSelected: false,
      },
      {
        key: 'last30days',
        label: 'Last 30 days',
        value: 30,
        isSelected: false,
      },
      {
        key: 'lastWeek',
        label: 'Last Week',
        value: '-week',
        isSelected: false,
      },
      {
        key: 'lastMonth',
        label: 'Last Month',
        value: '-month',
        isSelected: false,
      },
    ],
  },
  overlay: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'picker-shown', 'picker-hidden']) // Declare emit here


const dateRange = ref(props.modelValue)
const isPickerOpen = ref(false)

const mutableValue = computed({
  get() {
    return {
      start: props.modelValue.start,
      end: props.modelValue.end,
    }
  },
  set(value) {
    delete value.shortcut
    props.modelValue.start = value.start
    props.modelValue.end = value.end
    if(value.start && value.end) {
      isPickerOpen.value = false
    }
    updateValue(props.modelValue)
  },
})

const displayValue = computed(() => {
  if (props.modelValue?.start && props.modelValue?.end) {
    const startDate = new Date(props.modelValue.start)
    const endDate = new Date(props.modelValue.end)
    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
  }
  return ''
})

const updateValue = (value) => {
  emit('update:modelValue', value)
}


</script>

<style type="postcss">
.range {
  height: auto;
}


.datetimepicker, .datepicker, .datepicker-container, .pickers-container, .date-time-picker, .shortcuts-container {
  @apply !rounded-xl !border-none;
}
.shortcuts-container {
  min-width: 145px !important;
}

.datepicker {
  /* opacity: 0.2; */
  background: rgba(200,200,200, 0);
  @apply rounded-lg;
}

.datetimepicker.is-dark .datepicker {
  @apply dark:!bg-zinc-700/95;
  backdrop-filter: blur(3px); 

}

#date-preset-picker-input{
  @apply rounded-lg;
  font-weight: 500;
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
  height: 100%;
}




.datetimepicker.is-dark .pickers-container {
  background: transparent !important;
}

.shortcuts-container button.shortcut-button {
  margin-bottom: 5px !important;
}


#date-preset-picker-input{
  @apply border border-zinc-300 dark:border-base-100 text-base-content/80 text-xs;
  background: transparent;
  padding-left: 2.45rem !important;
}

.borderless #date-preset-picker-input{
  @apply !border-transparent dark:!border-transparent;
}


.date-time-picker label {
  @apply !pl-10 !text-base-content/60 dark:!text-base-content/50 !text-xs;
  left: 0rem !important;
  top: 0.75rem !important;
}




/* #date-preset-picker-input .field-label{
  padding-left: 3rem !important;
  left: 10rem !important;
  top: 0.75rem !important;
} */

.is-dark #date-preset-picker-input{
  border: 1px solid rgba(255,255,255,0);
  background: transparent;
  color: #FFF;
}

.is-dark.custom-button {
  background-color: transparent !important;
}
</style>